/**
 * PlatformMultiSelect — MultiSelect preconfigured with the visible channel-type
 * list and icon rendering (option rows + selected tags), so the "Social Platform"
 * filter can offer a checkbox-dropdown alternative to `PlatformIconGrid`'s icon
 * grid (see /settings' "Platform filter style" — some admins scan logos faster,
 * others prefer finding a platform by name in a compact list).
 */
import MultiSelect, { type MultiSelectOption } from '@/components/MultiSelect';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { getChannelTypeTitle } from '@/lib/utils/channelPlatformMeta';

function renderPlatformOption(opt: MultiSelectOption) {
  return (
    <span className="ms-option-row">
      <ChannelTypeIcon type={opt.value} size={14} tooltip={null} />
      <span className="ggc-text-body">{opt.label}</span>
    </span>
  );
}

// Icon only (no label) — with many platforms selected, the full-name tags ran out
// of trigger width almost immediately (a "+N" overflow chip after just 2-3
// selections). The remove "×" is rendered by MultiSelect itself, right after this.
function renderPlatformTag(opt: MultiSelectOption) {
  // No `tooltip` override — ChannelTypeIcon's own default (the platform name)
  // is exactly what an icon-only tag needs on hover.
  return <ChannelTypeIcon type={opt.value} size={12} />;
}

export interface PlatformMultiSelectProps {
  /** Every selectable platform type, in display order (e.g. `VISIBLE_CHANNEL_TYPES`). */
  types: string[];
  /** Currently-active types. */
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  portalled?: boolean;
}

export default function PlatformMultiSelect({
  types,
  value,
  onChange,
  placeholder,
  className,
  portalled,
}: PlatformMultiSelectProps) {
  const options: MultiSelectOption[] = types.map((type) => ({
    value: type,
    label: getChannelTypeTitle(type),
  }));
  return (
    <MultiSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={className}
      portalled={portalled}
      renderOption={renderPlatformOption}
      renderTag={renderPlatformTag}
      // The trigger sits in a narrow sidebar — never let the dropdown render
      // wider than it (MultiSelect's own default floors it at 240px).
      minDropdownWidth={0}
      showSelectAll
    />
  );
}
